import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Zap, Target } from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import { peecAPI } from '../api/peecAPI';
import { PageShell, PageHeader, SectionCard, TableFrame, Th, Td, Badge } from '../components/PeecUI';
import { LLMIcon } from '../components/LLMIcon';

interface InsightsPageProps {
  workspaceId: string;
}

const trendIcon = (trend: string) => {
  if (trend === 'up') return <TrendingUp size={15} className="text-emerald-500" />;
  if (trend === 'down') return <TrendingDown size={15} className="text-rose-500" />;
  return <Minus size={15} className="text-slate-400" />;
};

const impactBadge = (impact: string) => {
  if (impact === 'high') return <Badge tone="rose">高影响</Badge>;
  if (impact === 'medium') return <Badge tone="blue">中影响</Badge>;
  return <Badge>低影响</Badge>;
};

const effortBadge = (effort: string) => {
  if (effort === 'low') return <span className="text-xs text-emerald-600 font-medium">低投入</span>;
  if (effort === 'medium') return <span className="text-xs text-amber-600 font-medium">中投入</span>;
  return <span className="text-xs text-rose-600 font-medium">高投入</span>;
};

const InsightsPage: React.FC<InsightsPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [data, setData] = useState<any | null>(null);

  useEffect(() => {
    peecAPI.getInsights(workspaceId).then(setData).catch(console.error);
  }, [workspaceId]);

  if (!data) {
    return (
      <PageShell>
        <PageHeader title={t('pages.insightsTitle')} />
      </PageShell>
    );
  }

  const healthDelta = data.brand_health_score - 74;
  const visDelta = data.visibility_score - 68;

  return (
    <PageShell>
      <PageHeader title={t('pages.insightsTitle')} />

      {/* Score Cards */}
      <div className="mb-5 grid gap-4 grid-cols-1 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">品牌健康评分</div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${healthDelta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {healthDelta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {healthDelta >= 0 ? '+' : ''}{healthDelta} 较上周
            </span>
          </div>
          <div className="flex items-baseline gap-1.5 mb-4">
            <div className="text-4xl font-bold text-slate-900">{data.brand_health_score}</div>
            <div className="text-sm text-slate-400">/ 100</div>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-sky-500 transition-all duration-500"
              style={{ width: `${data.brand_health_score}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">综合品牌在 AI 平台的健康表现，含可见度、情感与引用质量。</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">AI 可见性评分</div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${visDelta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {visDelta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {visDelta >= 0 ? '+' : ''}{visDelta} 较上周
            </span>
          </div>
          <div className="flex items-baseline gap-1.5 mb-4">
            <div className="text-4xl font-bold text-slate-900">{data.visibility_score}</div>
            <div className="text-sm text-slate-400">/ 100</div>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-violet-500 transition-all duration-500"
              style={{ width: `${data.visibility_score}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">品牌在所有追踪 AI 模型中被引用和推荐的综合频率。</p>
        </div>
      </div>

      {/* Competitor snapshot + Sentiment */}
      <div className="mb-5 grid gap-4 grid-cols-1 xl:grid-cols-2">
        <SectionCard title="竞争概览">
          <div className="grid grid-cols-3 divide-x divide-slate-100 px-1 py-2">
            <div className="px-5 py-3">
              <div className="text-xs font-medium text-slate-400 mb-1">追踪竞品数</div>
              <div className="text-3xl font-bold text-slate-900">{data.competitor_analysis.competitors}</div>
            </div>
            <div className="px-5 py-3">
              <div className="text-xs font-medium text-slate-400 mb-1">竞品平均可见度</div>
              <div className="text-3xl font-bold text-slate-900">{data.competitor_analysis.avg_visibility}</div>
            </div>
            <div className="px-5 py-3">
              <div className="text-xs font-medium text-slate-400 mb-1">您的排名</div>
              <div className="text-3xl font-bold text-emerald-600">#{data.competitor_analysis.ranking_position}</div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="情感分布">
          <div className="px-5 py-4 space-y-3">
            {[
              { label: '正面', value: data.sentiment_analysis.positive, color: 'bg-emerald-500' },
              { label: '中性', value: data.sentiment_analysis.neutral, color: 'bg-slate-300' },
              { label: '负面', value: data.sentiment_analysis.negative, color: 'bg-rose-500' },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  <span className="text-sm font-semibold tabular-nums text-slate-900">{item.value}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${item.color} transition-all duration-500`} style={{ width: `${item.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* AI Model Breakdown */}
      <SectionCard title="各 AI 平台表现" className="mb-5">
        <TableFrame>
          <thead>
            <tr>
              <Th align="left">模型</Th>
              <Th>引用率</Th>
              <Th>平均排名</Th>
              <Th>情感得分</Th>
            </tr>
          </thead>
          <tbody>
            {data.ai_model_breakdown.map((row: any) => (
              <tr key={row.model} className="hover:bg-slate-50">
                <Td align="left">
                  <LLMIcon name={row.model} size={16} className="font-medium text-slate-900" />
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-sky-500 rounded-full" style={{ width: `${row.mention_rate}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums">{row.mention_rate}%</span>
                  </div>
                </Td>
                <Td>
                  <span className="font-semibold tabular-nums">#{row.avg_position.toFixed(1)}</span>
                </Td>
                <Td>
                  <span className={`font-semibold tabular-nums ${row.sentiment >= 70 ? 'text-emerald-600' : row.sentiment >= 55 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {row.sentiment}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </SectionCard>

      {/* Trending Keywords */}
      <SectionCard title="趋势关键词" className="mb-5">
        <div className="px-5 py-4 grid gap-2 sm:grid-cols-2">
          {data.trending_keywords.map((kw: any) => (
            <div key={kw.keyword} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
              <div>
                <div className="font-medium text-slate-900 text-sm">{kw.keyword}</div>
                <div className="text-xs text-slate-500 mt-0.5">月搜索量 {kw.volume.toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                {trendIcon(kw.trend)}
                <span className={`text-xs font-semibold tabular-nums ${kw.trend === 'up' ? 'text-emerald-600' : kw.trend === 'down' ? 'text-rose-600' : 'text-slate-400'}`}>
                  {kw.delta}
                </span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Opportunities */}
      <SectionCard title="优化机会">
        <div className="divide-y divide-slate-100">
          {data.top_opportunities.map((opp: any) => (
            <div key={opp.id} className="px-5 py-4 flex items-start gap-4 hover:bg-slate-50">
              <div className="mt-0.5 flex-shrink-0">
                {opp.impact === 'high'
                  ? <Zap size={18} className="text-amber-500" />
                  : <Target size={18} className="text-sky-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">{opp.title}</p>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {impactBadge(opp.impact)}
                    {effortBadge(opp.effort)}
                  </div>
                </div>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{opp.description}</p>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default InsightsPage;
